import type { Location } from 'react-router-dom';

/**
 * Determines if a URL matches the current location
 * Checks both pathname and query parameters
 *
 * @param to The target URL to check
 * @param location The current location from useLocation()
 * @returns boolean indicating if the URL is active
 */
export function isUrlActive(to: string, location: Location): boolean {
  // Handle empty hash link (link to the top of the page)
  if (to === '/#' || to === '/') {
    return location.pathname === '/' && (!location.hash || location.hash === '#');
  }

  // Handle root path with hash links (e.g., '/#pricing')
  if (to.startsWith('/#')) {
    return location.pathname === '/' && location.hash === to.substring(1);
  }

  const toUrl = new URL(to, window.location.origin);
  const toPathname = toUrl.pathname;
  const toSearch = toUrl.search;
  const toHash = toUrl.hash;

  // Check if location pathname starts with target pathname
  // This handles cases like '/category/agri-softs' matching '/category/agri-softs/all/weekly'
  const isPathMatch =
    location.pathname === toPathname || (toPathname !== '/' && location.pathname.startsWith(toPathname));

  // Check if search params match
  // If 'to' has query params, they should be included in location.search
  // If 'to' has no query params, location.search should also be empty
  const isSearchMatch = toSearch ? location.search.includes(toSearch.substring(1)) : !location.search;

  // Check if hash fragments match when specified
  const isHashMatch = toHash ? location.hash === toHash : true;

  return isPathMatch && isSearchMatch && isHashMatch;
}
